import type {
  AdminIntegration,
  IntegrationCallStatus,
  IntegrationCategory,
} from '@platform/contracts';
import type { BadgeTone } from '@platform/ui';

/**
 * The words and tones the integrations screens use (MASTER_PLAN M3.9, FR-ADMIN-09).
 *
 * Kept apart from the components so the list, the detail and the dashboard strip cannot
 * drift into three vocabularies for one state, and so the mapping is unit-testable
 * without rendering anything.
 *
 * Every label is a word, not only a colour (rules §48.5), and every one is plain: a
 * clinic manager reads "Not connected", not "unconfigured".
 */

/** The order the page groups categories in: what the clinic runs on, then what it measures. */
export const CATEGORY_ORDER: IntegrationCategory[] = [
  'ehr',
  'eligibility',
  'payment',
  'email',
  'ai_estimate',
  'analytics',
  'sms',
  'crm',
];

export const CATEGORY_LABELS: Record<IntegrationCategory, string> = {
  ehr: 'Practice management',
  eligibility: 'Insurance eligibility',
  payment: 'Payments',
  email: 'Email',
  ai_estimate: 'AI estimate',
  analytics: 'Analytics',
  sms: 'Text messages',
  crm: 'Marketing CRM',
};

export const CATEGORY_DESCRIPTIONS: Record<IntegrationCategory, string> = {
  ehr: 'Where appointments and patients are written.',
  eligibility: 'Checking a plan before the visit.',
  payment: 'Cards on file, charges and refunds.',
  email: 'Confirmations, reminders and staff alerts.',
  ai_estimate: 'Advisory copay estimates. Always reviewed by staff.',
  analytics: 'Booking conversions, sent from our servers with no identifiers.',
  sms: 'Reminders by text.',
  crm: 'Marketing contacts and consent, for patients who opted in.',
};

/**
 * What the status pill says. Seven states, and the first two are not health at all:
 * "Not enabled by platform" is the absent install, "Not connected" is an install with no
 * credentials. The rest are `integration_installs.health` (INTEGRATIONS.md §10.2).
 */
export type IntegrationStatus =
  'not_enabled' | 'not_connected' | 'configured' | 'healthy' | 'degraded' | 'failed';

export const STATUS_LABELS: Record<IntegrationStatus, string> = {
  not_enabled: 'Not enabled by platform',
  not_connected: 'Not connected',
  configured: 'Configured',
  healthy: 'Healthy',
  degraded: 'Degraded',
  failed: 'Failed',
};

export const STATUS_TONES: Record<IntegrationStatus, BadgeTone> = {
  not_enabled: 'neutral',
  not_connected: 'neutral',
  configured: 'info',
  healthy: 'success',
  degraded: 'warning',
  failed: 'danger',
};

/** One line under the pill saying what the state means and what happens next. */
export const STATUS_HINTS: Record<IntegrationStatus, string> = {
  not_enabled: 'Ask us to turn this on for your clinic.',
  not_connected: 'Add the credentials from your vendor account to connect it.',
  configured: 'Connected. The first scheduled check has not run yet.',
  healthy: 'The last check passed.',
  degraded: 'The last checks were slow or partly refused. It is still running.',
  failed: 'Work that depends on this is paused until it is fixed.',
};

export function statusOf(entry: AdminIntegration): IntegrationStatus {
  const { install } = entry;
  if (install === null || !install.enabledByPlatform) return 'not_enabled';
  if (!install.credentialsSet) return 'not_connected';
  if (install.health === 'unconfigured') return 'not_connected';
  return install.health;
}

export const CALL_STATUS_LABELS: Record<IntegrationCallStatus, string> = {
  ok: 'OK',
  error: 'Error',
  timeout: 'Timed out',
  rate_limited: 'Rate limited',
};

export const CALL_STATUS_TONES: Record<IntegrationCallStatus, BadgeTone> = {
  ok: 'success',
  error: 'danger',
  timeout: 'warning',
  rate_limited: 'warning',
};

export const HEALTH_RESULT_TONES: Record<'healthy' | 'degraded' | 'failed', BadgeTone> = {
  healthy: 'success',
  degraded: 'warning',
  failed: 'danger',
};

export const HEALTH_RESULT_LABELS: Record<'healthy' | 'degraded' | 'failed', string> = {
  healthy: 'Passed',
  degraded: 'Degraded',
  failed: 'Failed',
};

/**
 * A timestamp in the clinic's words. Absolute, not "3 minutes ago": an operator comparing
 * a health check against a vendor's status page needs a time they can match.
 */
export function formatMoment(value: string | null, timeZone = 'America/Los_Angeles'): string {
  if (value === null) return 'Never';
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return 'Unknown';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(at);
}

export function formatLatency(ms: number | null): string {
  if (ms === null) return '—';
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${String(ms)} ms`;
}
